import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RemitKycCard, StatusBadge } from "@/components/RemitKyc";
import { RemitAdminPanel } from "@/components/RemitAdminPanel";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send as SendIcon, Loader2, Plus, Trash2, CheckCircle2, ArrowRight, FlaskConical, Eye, EyeOff } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Send() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [country, setCountry] = useState("UG");
  const [amount, setAmount] = useState("50");
  const [quote, setQuote] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipientId, setRecipientId] = useState("");
  const [transfers, setTransfers] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newRec, setNewRec] = useState({ name: "", phone: "", method: "" });
  const [adminStats, setAdminStats] = useState(null);

  const corridor = useMemo(() => config?.corridors?.find((c) => c.code === country), [config, country]);
  const countryRecipients = useMemo(() => recipients.filter((r) => r.country === country), [recipients, country]);

  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const tid = params.get("transfer");
    if (!tid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/remit/transfers/${tid}`);
        setDone(data);
        setTransfers((list) => [data, ...list.filter((x) => x.id !== data.id)]);
        if (["awaiting_payment", "paid", "processing"].includes(data.status) && tries++ < 20) setTimeout(poll, 3000);
      } catch { /* ignore */ }
    };
    poll();
    setParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/remit/config").then(({ data }) => setConfig(data)).catch(() => {});
    api.get("/remit/recipients").then(({ data }) => setRecipients(data.recipients)).catch(() => {});
    api.get("/remit/transfers").then(({ data }) => setTransfers(data.transfers)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/remit/admin/overview").then(({ data }) => setAdminStats(data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0 || !country) { setQuote(null); return; }
    const t = setTimeout(() => {
      api.post("/remit/quote", { amount_usd: amt, country })
        .then(({ data }) => setQuote(data))
        .catch(() => setQuote(null));
    }, 350);
    return () => clearTimeout(t);
  }, [amount, country]);

  useEffect(() => {
    setRecipientId(countryRecipients[0]?.id || "");
  }, [country, recipients]); // eslint-disable-line

  const addRecipient = async () => {
    try {
      const { data } = await api.post("/remit/recipients", {
        ...newRec, country, method: newRec.method || corridor?.methods?.[0] || "",
      });
      setRecipients((r) => [...r, data.recipient]);
      setRecipientId(data.recipient.id);
      setShowAdd(false);
      setNewRec({ name: "", phone: "", method: "" });
      toast.success("Recipient saved");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const removeRecipient = async (id) => {
    await api.delete(`/remit/recipients/${id}`).catch(() => {});
    setRecipients((r) => r.filter((x) => x.id !== id));
  };

  const sendNow = async () => {
    if (!recipientId || !quote) return;
    setSending(true);
    try {
      const { data } = await api.post("/remit/send", { recipient_id: recipientId, amount_usd: quote.amount_usd });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      setDone(data.transfer);
      setTransfers((t) => [data.transfer, ...t]);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const toggleVisibility = async () => {
    const next = !adminStats.enabled;
    await api.put("/remit/admin/toggle", { enabled: next }).catch(() => {});
    setAdminStats((s) => ({ ...s, enabled: next }));
    toast.success(next ? "Send Money is now visible to users" : "Send Money hidden from users");
  };

  if (config && !config.enabled && user?.role !== "admin") {
    return (
      <div className="max-w-md mx-auto text-center py-16" data-testid="remit-coming-soon">
        <SendIcon className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
        <h1 className="font-heading text-xl font-bold">Send Money — coming soon</h1>
        <p className="text-sm text-zinc-500 mt-2">We're working with licensed payout partners to bring you low-cost transfers to Africa and beyond.</p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-white border border-black/10 rounded-2xl p-8 shadow-sm text-center" data-testid="remit-success-screen">
          {done.status === "delivered" ? <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" /> : <Loader2 className="h-14 w-14 text-primary mx-auto mb-3 animate-spin" />}
          <h1 className="font-heading text-xl font-bold">
            {done.status === "delivered" ? "Transfer delivered" : done.status === "awaiting_payment" ? "Waiting for your payment" : done.status === "payout_failed" ? "Payout failed" : "Transfer on its way"}
          </h1>
          <div className="mt-2"><StatusBadge status={done.status} /></div>
          {done.simulated && <p className="text-xs font-bold text-amber-600 uppercase tracking-wide mt-2">Test payout — no money reaches the recipient yet</p>}
          <div className="mt-5 bg-zinc-50 border border-black/5 rounded-xl p-4 text-left space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-zinc-500">To</span><span className="font-semibold">{done.flag} {done.recipient_name}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Via</span><span className="font-semibold">{done.method}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">They receive</span><span className="font-mono font-bold text-emerald-600" data-testid="remit-success-amount">{done.symbol} {done.receive_amount.toLocaleString()}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">You paid</span><span className="font-mono font-semibold">${done.total_paid_usd.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Reference</span><span className="font-mono font-semibold" data-testid="remit-reference">{done.reference}</span></div>
          </div>
          <button onClick={() => setDone(null)} className="mt-6 w-full h-12 rounded-full bg-primary text-white font-bold active:scale-95 transition-transform" data-testid="remit-send-another">
            Send another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-heading text-xl font-bold tracking-tight">Send Money</h1>
        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 px-2.5 py-1 rounded-full" data-testid="remit-demo-badge">
          <FlaskConical className="h-3 w-3" /> Demo
        </span>
      </div>
      {config?.live_money
        ? <p className="text-xs text-zinc-500 mb-4">Pay by card, delivered to mobile money or bank. Powered by ZeeptelSend.</p>
        : <p className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-4" data-testid="remit-test-mode">Test mode: card payments are real but payouts are simulated until our payout partner is connected.</p>}
      {config && config.kyc_status !== "approved" && (
        <div className="mb-4"><RemitKycCard status={config.kyc_status} note={config.kyc_note} onSubmitted={() => setConfig((c) => ({ ...c, kyc_status: "pending" }))} /></div>
      )}

      {user?.role === "admin" && <RemitAdminPanel />}
      {user?.role === "admin" && adminStats && (
        <div className="mb-4 bg-zinc-900 text-white rounded-2xl p-4 text-sm" data-testid="remit-admin-panel">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-bold">Admin — ZeeptelSend</div>
              <div className="text-xs text-zinc-400 mt-0.5">{adminStats.count} transfers · ${adminStats.volume_usd} volume · ${adminStats.fees_usd} fees</div>
            </div>
            <button onClick={toggleVisibility} data-testid="remit-admin-toggle"
              className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-full active:scale-95 transition-transform ${adminStats.enabled ? "bg-emerald-500" : "bg-zinc-600"}`}>
              {adminStats.enabled ? <><Eye className="h-3.5 w-3.5" /> Visible</> : <><EyeOff className="h-3.5 w-3.5" /> Hidden</>}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-black/10 rounded-2xl p-4 shadow-sm">
        <Select value={country} onValueChange={setCountry}>
          <SelectTrigger className="w-full h-11 rounded-xl border-black/10 bg-white mb-3 text-base" data-testid="remit-country-select">
            <SelectValue placeholder="Where to?" />
          </SelectTrigger>
          <SelectContent>
            {(config?.corridors || []).map((c) => (
              <SelectItem key={c.code} value={c.code} data-testid={`remit-country-${c.code}`}>
                <span className="flex items-center gap-2"><span className="text-lg">{c.flag}</span><span className="font-medium">{c.name}</span><span className="text-zinc-400 text-xs">{c.currency}</span></span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2 bg-zinc-50 border border-black/5 rounded-xl px-3 py-2 mb-3">
          <span className="text-zinc-400 font-bold">$</span>
          <input
            type="number" min="1" max="2000" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="flex-1 bg-transparent outline-none font-mono-dial text-2xl"
            data-testid="remit-amount-input"
          />
          <span className="text-xs font-bold text-zinc-400 uppercase">USD</span>
        </div>

        {quote && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-3 text-sm" data-testid="remit-quote-card">
            <div className="flex items-center justify-between">
              <span className="text-zinc-600">They receive</span>
              <span className="font-mono-dial text-xl font-bold text-primary" data-testid="remit-quote-receive">{quote.symbol} {quote.receive_amount.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-xs text-zinc-500 mt-1.5">
              <span>Rate: 1 USD = {quote.fx_rate.toLocaleString()} {quote.currency}</span>
              <span>Fee: ${quote.fee_usd.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between text-xs mt-1">
              <span className="text-zinc-500">You pay</span>
              <span className="font-bold">${quote.total_pay_usd.toFixed(2)}</span>
            </div>
          </div>
        )}

        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-zinc-400">Recipient</span>
            <button onClick={() => setShowAdd((v) => !v)} className="flex items-center gap-1 text-xs font-bold text-primary" data-testid="remit-add-recipient-btn">
              <Plus className="h-3.5 w-3.5" /> New
            </button>
          </div>

          {showAdd && (
            <div className="bg-zinc-50 border border-black/5 rounded-xl p-3 mb-2 space-y-2" data-testid="remit-add-recipient-form">
              <input placeholder="Full name" value={newRec.name} onChange={(e) => setNewRec({ ...newRec, name: e.target.value })}
                className="w-full h-10 rounded-lg border border-black/10 px-3 text-sm" data-testid="remit-recipient-name" />
              <input placeholder={`Phone (${corridor?.name || ""})`} value={newRec.phone} onChange={(e) => setNewRec({ ...newRec, phone: e.target.value })}
                className="w-full h-10 rounded-lg border border-black/10 px-3 text-sm" data-testid="remit-recipient-phone" />
              <Select value={newRec.method || corridor?.methods?.[0] || ""} onValueChange={(v) => setNewRec({ ...newRec, method: v })}>
                <SelectTrigger className="w-full h-10 rounded-lg border-black/10 bg-white text-sm" data-testid="remit-recipient-method">
                  <SelectValue placeholder="Payout method" />
                </SelectTrigger>
                <SelectContent>
                  {(corridor?.methods || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <button onClick={addRecipient} className="w-full h-10 rounded-full bg-zinc-900 text-white text-sm font-bold active:scale-95 transition-transform" data-testid="remit-save-recipient">
                Save recipient
              </button>
            </div>
          )}

          {countryRecipients.length === 0 && !showAdd && (
            <p className="text-xs text-zinc-400" data-testid="remit-no-recipients">No recipients for {corridor?.name} yet — tap New to add one.</p>
          )}
          {countryRecipients.map((r) => (
            <div key={r.id}
              onClick={() => setRecipientId(r.id)}
              className={`flex items-center justify-between px-3 py-2.5 rounded-xl mb-1 cursor-pointer border transition-colors ${recipientId === r.id ? "border-primary bg-blue-50" : "border-black/5 bg-white hover:bg-zinc-50"}`}
              data-testid={`remit-recipient-${r.id}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{r.name}</div>
                <div className="text-xs text-zinc-400 font-mono">{r.phone} · {r.method}</div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); removeRecipient(r.id); }} className="text-zinc-300 hover:text-red-500 transition-colors" data-testid={`remit-delete-recipient-${r.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="px-14 sm:px-0">
          <button
            onClick={sendNow}
            disabled={sending || !quote || !recipientId}
            className="w-full h-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30 transition-transform active:scale-95 disabled:opacity-50"
            data-testid="remit-send-button"
          >
            {sending ? <Loader2 className="h-6 w-6 animate-spin" /> : <SendIcon className="h-5 w-5" />} Send {quote ? `$${quote.total_pay_usd.toFixed(2)}` : ""}
          </button>
        </div>
      </div>

      {transfers.length > 0 && (
        <div className="mt-6 bg-white border border-black/10 rounded-2xl p-5 shadow-sm" data-testid="remit-transfers-list">
          <h2 className="font-heading font-bold mb-3">Recent transfers</h2>
          {transfers.slice(0, 8).map((t) => (
            <div key={t.id} className="flex items-center justify-between py-2.5 border-b border-black/5 last:border-0">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-2xl">{t.flag}</span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{t.recipient_name}</div>
                  <div className="text-xs text-zinc-400">{t.reference} · {new Date(t.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-sm font-bold">{t.symbol} {t.receive_amount.toLocaleString()}</div>
                <div className="flex justify-end mt-0.5"><StatusBadge status={t.status} /></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
